import React, { useRef, useState } from 'react';
import ControlViewModel from '../../control.viewmodel';
import { IControlRoomDTO, TCopyPodiumLinkFrom } from '../../../../../../api/control.dao';
import { ConnectionType, IConnectionDetailsDTO, IDeleteConnectionRequestParams } from '../../../../../../api/connections.dao';
import { ControlFormListOfConnections } from '../../_control-form/_control-form-connections';
import Text from '../../../../../global/text/text';
import { getCompleteRoomName } from '../../../../../../infrastructure/utils/common.utils';
import { WebinarReportsViewModel } from '../../../shared/webinar-report/webinar-reports.viewmodel';
import {CONNECTION_DETAILS_POPOVER_TRACKING_EVENTS, OpenFrom, TRACKING_EVENTS} from '../../../../../../tracking-service/tracking-values';
import { MeetingApiParams } from '../../../shared/connections-list/_connection-form/connection-form.viewmodel';
import { IDeleteConnectionDetails } from '../../../shared/connections-list/_connection-delete-confirm-popover';
import { ConnectionVersion } from '../../../../../../constants/common.constants';
import { PodiumLinkPopover } from '../../../../../global/podium-link-popover/podium-link-popover';
import cn from 'classnames';

interface IProps {
    controlVM: ControlViewModel;
    room: IControlRoomDTO;
    eventTimezone: string;
    meetingId: number;
    connections: IConnectionDetailsDTO[];
    defaultMeetingConnectionId?: number;
    webinarReportsVM?: WebinarReportsViewModel;
    getRecordMeetingText: (meetingId: number) => JSX.Element | null;
    connectionVersion?: ConnectionVersion;
    podiumLinkEnabled: boolean;
    handlePodiumLinkFailure: (e: Error) => void;
    trackCopyPodiumLinkAction: (from: TCopyPodiumLinkFrom, meetingId: number) => void;
}

export const ControlListItemConnections = ({
    controlVM,
    meetingId,
    room,
    eventTimezone,
    connections: meetingConnections,
    defaultMeetingConnectionId,
    webinarReportsVM,
    getRecordMeetingText,
    connectionVersion,
    podiumLinkEnabled,
    handlePodiumLinkFailure,
    trackCopyPodiumLinkAction,
}: IProps) => {
    const [isPopoverOpen, handleIsOpenPopover] = useState<boolean>(false);
    const buttonItemRef = useRef<HTMLDivElement>(null);
    const roomConnections = room?.connections ?? [];
    const roomId = room?.roomId;

    const meetingParams: MeetingApiParams = {
        accountId: controlVM.getAccountId(),
        eventId: controlVM.getEvent().eventId,
        meetingId: meetingId,
    };

    return (
        <div className='connection-list m-bottom-0px'>
            {getRecordMeetingText(meetingId)}
            {podiumLinkEnabled &&
                <>
                    <div className='connection-list__label-wrapper'>
                        <div className='connection-list__label'>
                            <Text message='Podium' withEllipsis />
                        </div>
                    </div>
                    <div
                        className='connection-list__connection-wrapper'
                        ref={buttonItemRef}
                    >
                        <button
                            className={cn('connection-list__item connection-list__item--action connection-list__item--not-started', {
                                'connection-list__item--active': isPopoverOpen,
                            })}
                            onClick={e => {
                                e.preventDefault();
                                handleIsOpenPopover(isOpen => !isOpen);
                            }}
                        >
                            <Text message='Podium link' withEllipsis />
                        </button>
                        <PodiumLinkPopover
                            buttonRef={buttonItemRef}
                            isOpen={isPopoverOpen}
                            getPodiumLink={() =>controlVM.getPodiumLink(meetingParams)}
                            onClose={() => handleIsOpenPopover(false)}
                            handlePodiumLinkFailure={handlePodiumLinkFailure}
                            onCopy={() => trackCopyPodiumLinkAction('Control tab', +meetingParams.meetingId)}
                        />
                    </div>
                </>
            }
            {room?.connections?.length > 0 && (
                <>
                    <div className='connection-list__label-wrapper p-top-5px'>
                        <div className='connection-list__label'>
                            <Text
                                data-testid='control-list-item-room'
                                className='control-list-item__text'
                                message={room ? getCompleteRoomName({ name: room.roomName, label: room.roomLabel }) : ''}
                                withEllipsis
                            />
                        </div>
                    </div>
                    <ControlFormListOfConnections
                        eventTimezone={eventTimezone}
                        connections={roomConnections}
                        cloneConnection={controlVM.handleCloneConnection}
                        onChange={connection =>
                            controlVM.openPopupEditConnection(connection, meetingId, roomId)
                        }
                        showConfigurePassportForm={connection => {
                            controlVM.showConfigurePassportForm({
                                accountId: controlVM.getAccountId(),
                                eventId: controlVM.getEvent().eventId,
                                roomId: roomId,
                                meetingConnectionId: connection.meetingConnectionId
                            }, connection);
                        }}
                        onDelete={(data: IDeleteConnectionRequestParams, connectionDetails: IDeleteConnectionDetails) =>
                            controlVM.onDeleteRoomConnection(data, meetingId, roomId, connectionDetails)
                        }
                        defaultConnectionId={room?.defaultConnection?.meetingConnectionId}
                        controlledCheckbox={false}
                        checkbox={true}
                        webinarReportsVM={webinarReportsVM}
                        handleTrackingEvent = {(event: CONNECTION_DETAILS_POPOVER_TRACKING_EVENTS) =>
                            controlVM.trackEventMixPanel(
                                TRACKING_EVENTS.CONTROL_PAGE_CONNECTION_DETAILS_POPOVER,
                                event
                            )}
                            handleTroubleshoot={(connection: IConnectionDetailsDTO) => {
                                controlVM.handleTroubleshoot(connection, OpenFrom.Control);
                            }}
                            trackHostJoinClick={(eventName: CONNECTION_DETAILS_POPOVER_TRACKING_EVENTS) => {
                                controlVM.trackHostJoinClick(eventName, OpenFrom.Control);
                            }}
                        meetingParams={meetingParams}
                        hideChevron
                        shouldDisableDeleteButton={controlVM.shouldDisableDeleteButton}
                    />
                </>
            )}

            {meetingConnections.length > 0 && (
                <>
                    <div className='connection-list__label-wrapper p-top-5px'>
                        <div className='connection-list__label'>Meeting Connections</div>
                    </div>
                    <ControlFormListOfConnections
                        eventTimezone={eventTimezone}
                        connections={meetingConnections.filter(c => c.type !== ConnectionType.UniqueJoiningLink)}
                        cloneConnection={controlVM.handleCloneConnection}
                        onChange={connection =>
                            controlVM.openPopupEditConnection(connection, meetingId)
                        }
                        showConfigurePassportForm={connection => {
                            controlVM.showConfigurePassportForm({
                                accountId: controlVM.getAccountId(),
                                eventId: controlVM.getEvent().eventId,
                                meetingId: meetingId,
                                meetingConnectionId: connection.meetingConnectionId
                            }, connection);
                        }}
                        onDelete={(data: IDeleteConnectionRequestParams, connectionDetails: IDeleteConnectionDetails) =>
                            controlVM.onDeleteMeetingConnection(data, meetingId, connectionDetails)
                        }
                        defaultConnectionId={defaultMeetingConnectionId}
                        checkbox={true}
                        webinarReportsVM={webinarReportsVM}
                        handleTrackingEvent = {(event: CONNECTION_DETAILS_POPOVER_TRACKING_EVENTS) =>
                            controlVM.trackEventMixPanel(
                                TRACKING_EVENTS.CONTROL_PAGE_CONNECTION_DETAILS_POPOVER,
                                event
                            )}
                            handleTroubleshoot={(connection: IConnectionDetailsDTO) => {
                                controlVM.handleTroubleshoot(connection, OpenFrom.Control);
                            }}
                            trackHostJoinClick={(eventName: CONNECTION_DETAILS_POPOVER_TRACKING_EVENTS) => {
                                controlVM.trackHostJoinClick(eventName, OpenFrom.Control);
                            }}
                        meetingParams={meetingParams}
                        hideChevron
                        shouldDisableDeleteButton={controlVM.shouldDisableDeleteButton}
                        connectionVersion={connectionVersion}
                        openFrom={OpenFrom.Control}
                    />
                </>
            )}
        </div>
    );
};
